import {
    forwardRef,
    ReactElement,
    Suspense,
    useCallback,
    useImperativeHandle,
    useMemo,
    useState,
} from 'react';
import { Link, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import {
    Box,
    Typography,
    Collapse,
    CircularProgress,
    Theme,
    Stack,
    SvgIconProps,
    IconButton,
} from '@mui/material';
import { BoxTypeMap } from '@mui/system';
// import { ProfileState } from 'state/profile/Reducer';
import { AuthActions } from 'state/authentication/Action';
// import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
// import ArrowDropUpIcon from '@material-ui/icons/ArrowDropUp';
// import AzureAD, { IAzureADFunctionProps } from 'react-aad-msal';
// import { azureAdProps } from 'shared/hooks/AADProvider';
import { AuthenticationService } from 'services';

import { Toast, Utils } from 'shared/helpers';
// eslint-disable-next-line import/no-unresolved
import { OverridableComponent } from '@mui/material/OverridableComponent';
import {
    DashboardFilled,
    Dashboard,
    UploadIcon,
    FolderIcon,
    ChecklistIcon,
    UserIcon,
    UserDetailIcon,
    ChevronDown,
    ChevronUp,
    SideboardIcon,
} from 'shared/components/AppIcons';
import { useMeasure } from 'react-use';
import { AxiosError } from 'axios';

type BoxInTabIndex = {
    disabled?: boolean;
    tabIndex?: string;
    component: any;
};

type BoxLoggingOut = OverridableComponent<
    BoxTypeMap<BoxInTabIndex, 'div', Theme>
>;

const LogoutButton: BoxLoggingOut = Box;

export interface IAppSidebarInnerRef {
    logoutPortal: any;
}

export enum EMenuType {
    NAVIGATION = 'navigation',
    ONCLICK = 'onClick',
}

export interface IMenuItems {
    path: string;
    title: string;
    icon: ReactElement;
    key: string;
    navigationType: EMenuType;
    superFlag: any;
    childrenPath?: Array<string>;
}

export interface IAppSidebar {
    children: React.ReactNode;
    menus: IMenuItems[];
    ref: React.Ref<IAppSidebarInnerRef>;
    defaultLogo: string;
    defaultLogoLink: string;
    onMenuChange?: (menu: IMenuItems) => void;
    onUserDetailClick: () => void;
    // logoutUser: (payload: any) => void;
    // baseAppState: BaseApplicationState;
}

const SuspenseTrigger = (): ReactElement => {
    throw new Promise(() => {});
};

interface IMenuProps extends SvgIconProps {
    menukey: string;
    active: boolean;
}
const MenuIconItem = (menu: IMenuProps): ReactElement => {
    const { menukey, active, ...menuProps } = menu;
    if (menukey === 'dashboard' && active === false) {
        return <Dashboard {...menuProps} />;
    }
    if (menukey === 'dashboard' && active === true) {
        return <DashboardFilled {...menuProps} />;
    }
    if (menukey === 'upload-packets') {
        return <UploadIcon {...menuProps} />;
    }
    if (menukey === 'manage-packets') {
        return <FolderIcon {...menuProps} />;
    }
    if (menukey === 'manage-checklists') {
        return <ChecklistIcon {...menuProps} />;
    }
    if (menukey === 'manage-access') {
        return <UserIcon {...menuProps} />;
    }
    return <Dashboard {...menuProps} />;
};

const AppSidebarV2: React.FC<IAppSidebar> = forwardRef(
    (props, ref): ReactElement => {
        const {
            children,
            menus,
            defaultLogo,
            defaultLogoLink,
            onMenuChange,
            onUserDetailClick,
        } = props;
        const [menuRef, { width }] = useMeasure();
        const profileState = useSelector((state: any) => state.profileState);
        const { userInfo } = profileState;
        const navigate = useNavigate();
        const dispatch = useDispatch();
        const location = useLocation();
        const [menuBoard, setMenuBoard] = useState<string>('normal');
        const [openLoggedUserMenu, setOpenLoggedUserMenu] = useState(false);
        const [isLogoff, setLogoff] = useState<boolean>(false);
        const [isLoggingOut, setLoggingOut] = useState<boolean>(false);

        const isUserActive = useMemo(
            () =>
                userInfo &&
                typeof userInfo.role === 'string' &&
                userInfo.role !== ''
                    ? userInfo.role
                    : null,
            [userInfo]
        );

        const logoutPortal: VoidFunction = (): void => {
            console.log('------IdleCheck: AppSidebarV2 file logoutPortal');
            setLoggingOut(true);
            AuthenticationService.logout()
                .then((response) => {
                    Utils.handleSuccessResponse(response);
                    setLogoff(true);
                    setLoggingOut(false);
                    dispatch(AuthActions.doLogout({}));
                    console.log(
                        '------IdleCheck: AppSidebarV2 file stopIdleCheck'
                    );

                    navigate('/login', { replace: true });
                })
                .catch((error) => {
                    const { response } = error;

                    if (
                        error !== null &&
                        error instanceof AxiosError &&
                        error.code === 'ERR_NETWORK'
                    ) {
                        Toast.showErrorMessage(
                            'No internet connection, Please try again!'
                        );
                    } else if (
                        response &&
                        response.status &&
                        response.status === 401
                    ) {
                        const { data } = response;
                        Toast.showErrorMessage(data.msg);

                        dispatch(AuthActions.doLogout({}));
                    }

                    setLogoff(false);
                    setLoggingOut(false);
                });
        };

        useImperativeHandle(ref, () => ({ logoutPortal }), []);

        // UI Actions
        const actionOnMenuClick = (menu: IMenuItems): void => {
            if (onMenuChange) onMenuChange(menu);
        };

        const isValidChildrenPath = useCallback(
            (childrenPath: Array<string> | undefined) =>
                childrenPath &&
                childrenPath.includes(location.pathname) === true,
            [location]
        );

        const handleMenuTitle = useCallback(
            (title: any, key: string) => {
                if (key === 'upload-packets' && menuBoard !== 'normal') {
                    // return 'Upload';
                    return title;
                }

                if (key === 'manage-access' && menuBoard !== 'normal') {
                    // return 'Manage';
                    return title;
                }
                return title;
            },
            [menuBoard]
        );
        // const parseRole = useCallback((role: string) => {
        //     if (role === 'clientManager') {
        //         return 'Client manager';
        //     }
        //     if (role === 'senior') {
        //         return 'Senior';
        //     }
        //     return 'Operator';
        // }, []);

        // UI Elements
        return (
            <Box className="">
                <Box className={`side-menu-v2 ${menuBoard}`}>
                    <Box className="menu-container">
                        <Box className="sidebar-board-icon">
                            <IconButton
                                onClick={() => {
                                    setMenuBoard(
                                        menuBoard === 'normal'
                                            ? 'minimize'
                                            : 'normal'
                                    );
                                }}
                            >
                                <SideboardIcon className="board-icon" />
                            </IconButton>
                        </Box>
                        {defaultLogoLink.startsWith('http') && (
                            <a
                                href={defaultLogoLink}
                                target="externalsite"
                                onKeyUp={(e) => {
                                    if (e.key === 'Tab') {
                                        setOpenLoggedUserMenu(true);
                                    }
                                }}
                            >
                                <img
                                    // className="logo-img"
                                    src={defaultLogo}
                                    alt="Peppermoney logo"
                                    height={50}
                                    width={50}
                                />
                            </a>
                        )}

                        {!defaultLogoLink.startsWith('http') && (
                            <Link
                                to={defaultLogoLink}
                                className="logo"
                                onKeyUp={(e) => {
                                    if (e.key === 'Tab') {
                                        setOpenLoggedUserMenu(true);
                                    }
                                }}
                            >
                                <img
                                    height={23}
                                    width={183}
                                    src={defaultLogo}
                                    alt="Peppermoney Logo"
                                />
                            </Link>
                        )}

                        {/* <OrganisationList {...props} /> */}

                        <Box className="menus" role="navigation">
                            {menus
                                .filter(
                                    (d) =>
                                        d.key === 'dashboard' ||
                                        d.key === 'upload-packets'
                                )
                                .map((menu: IMenuItems) => (
                                    <>
                                        {menu.navigationType ===
                                            EMenuType.NAVIGATION && (
                                            <NavLink
                                                className="menu-link"
                                                to={menu.path}
                                                onClick={() =>
                                                    actionOnMenuClick(menu)
                                                }
                                            >
                                                {({ isActive }) => (
                                                    <Stack
                                                        direction={
                                                            menuBoard ===
                                                            'normal'
                                                                ? 'row'
                                                                : 'column'
                                                        }
                                                        className="menu-navigation-stack"
                                                    >
                                                        <MenuIconItem
                                                            className={`menu-icon ${menu.key}`}
                                                            menukey={menu.key}
                                                            active={isActive}
                                                        />
                                                        <Typography
                                                            className={
                                                                isActive ||
                                                                isValidChildrenPath(
                                                                    menu.childrenPath
                                                                )
                                                                    ? `active-menu menu ${menu.key}`
                                                                    : `menu ${menu.key}`
                                                            }
                                                        >
                                                            <Box>
                                                                {handleMenuTitle(
                                                                    menu.title,
                                                                    menu.key
                                                                )}
                                                                {menu.superFlag}
                                                            </Box>
                                                        </Typography>
                                                    </Stack>
                                                )}
                                            </NavLink>
                                        )}
                                        {menu.navigationType ===
                                            EMenuType.ONCLICK && (
                                            <Typography
                                                className="menu cursor-pointer"
                                                onClick={() =>
                                                    actionOnMenuClick(menu)
                                                }
                                            >
                                                {menu.title}
                                                {/* <Box className="menu-icon">{menu.icon}</Box> */}
                                            </Typography>
                                        )}
                                    </>
                                ))}
                        </Box>
                        {menus && menus.length > 0 && (
                            <Typography className="manage-sub-menu">
                                Manage
                            </Typography>
                        )}
                        <Box className="menus sub-menu" role="navigation">
                            {menus
                                .filter(
                                    (d) =>
                                        d.key !== 'dashboard' &&
                                        d.key !== 'upload-packets'
                                )
                                .map((menu: IMenuItems) => (
                                    <>
                                        {menu.navigationType ===
                                            EMenuType.NAVIGATION && (
                                            <NavLink
                                                className="menu-link"
                                                to={menu.path}
                                                onClick={() =>
                                                    actionOnMenuClick(menu)
                                                }
                                            >
                                                {({ isActive }) => (
                                                    <Stack
                                                        direction={
                                                            menuBoard ===
                                                            'normal'
                                                                ? 'row'
                                                                : 'column'
                                                        }
                                                        className="menu-navigation-stack"
                                                    >
                                                        <MenuIconItem
                                                            className={`menu-icon ${menu.key}`}
                                                            menukey={menu.key}
                                                            active={isActive}
                                                        />
                                                        <Typography
                                                            className={
                                                                isActive ||
                                                                isValidChildrenPath(
                                                                    menu.childrenPath
                                                                )
                                                                    ? `active-menu menu ${menu.key}`
                                                                    : `menu ${menu.key}`
                                                            }
                                                        >
                                                            <Box>
                                                                {handleMenuTitle(
                                                                    menu.title,
                                                                    menu.key
                                                                )}
                                                                {menu.superFlag}
                                                            </Box>
                                                        </Typography>
                                                    </Stack>
                                                )}
                                            </NavLink>
                                        )}
                                        {menu.navigationType ===
                                            EMenuType.ONCLICK && (
                                            <Typography
                                                className="menu cursor-pointer"
                                                onClick={() =>
                                                    actionOnMenuClick(menu)
                                                }
                                            >
                                                {menu.title}
                                                {/* <Box className="menu-icon">{menu.icon}</Box> */}
                                            </Typography>
                                        )}
                                    </>
                                ))}
                        </Box>
                    </Box>
                    <Box
                        className="login-info-container"
                        role="status"
                        aria-live="off"
                    >
                        <Box id="collapsible-status-user">
                            <Collapse
                                in={openLoggedUserMenu}
                                className="open-menu"
                                orientation="vertical"
                            >
                                <LogoutButton
                                    sx={{ width: `${width + 20}px` }}
                                    disabled={isLoggingOut}
                                    className="user-menu-first"
                                    onClick={logoutPortal}
                                    component="button"
                                    tabIndex="-1"
                                    aria-pressed={isLogoff ? 'true' : 'false'}
                                    aria-labelledby="logout-typography"
                                >
                                    <Typography
                                        id="logout-typography"
                                        tabIndex="0"
                                    >
                                        Logout
                                    </Typography>
                                </LogoutButton>

                                <Box
                                    sx={{ width: `${width + 20}px` }}
                                    className={
                                        location.pathname === '/user/details' ||
                                        location.pathname === '/user/security'
                                            ? 'user-menu-last active-menu'
                                            : 'user-menu-last'
                                    }
                                >
                                    <Typography
                                        component={NavLink}
                                        to="/user/details"
                                        onClick={onUserDetailClick}
                                    >
                                        User details
                                    </Typography>
                                </Box>
                                {/* <Box className="user-menu">
                            <Typography
                                component={NavLink}
                                to="/user/details"
                                //   activeClassName="active-user-menu"
                            >
                                User details & Password
                            </Typography>
                        </Box> */}
                            </Collapse>
                        </Box>
                        <Stack
                            ref={menuRef}
                            direction={
                                menuBoard === 'normal' ? 'row' : 'column'
                            }
                            className={
                                !openLoggedUserMenu
                                    ? `login-user ${menuBoard}`
                                    : `login-user open-menu ${menuBoard}`
                            }
                            onClick={() => {
                                setOpenLoggedUserMenu(!openLoggedUserMenu);
                            }}
                            role="button"
                            aria-controls="collapsible-status-user"
                            aria-expanded={openLoggedUserMenu}
                        >
                            <Box>
                                {menuBoard === 'minimize' ? (
                                    <UserDetailIcon className="userdetail-icon" />
                                ) : (
                                    <Suspense
                                        fallback={
                                            <Box className="align-loader">
                                                <CircularProgress
                                                    title="progress loading"
                                                    className="mr-2 margin-loader useractivity"
                                                    size={24}
                                                    color="info"
                                                />
                                            </Box>
                                        }
                                    >
                                        {isUserActive ? (
                                            <Typography className="logged-user-container">
                                                Logged in as
                                            </Typography>
                                        ) : (
                                            <SuspenseTrigger />
                                        )}
                                    </Suspense>
                                )}

                                {menuBoard !== 'minimize' && (
                                    <Typography className="user-name">
                                        {userInfo.name.first}{' '}
                                        {userInfo.name.last}
                                    </Typography>
                                )}
                            </Box>
                            <Box>
                                {openLoggedUserMenu && (
                                    <ChevronDown className="userdetail-nav" />
                                )}
                                {!openLoggedUserMenu && (
                                    <ChevronUp className="userdetail-nav" />
                                )}
                            </Box>
                        </Stack>
                    </Box>
                </Box>
                {children}
            </Box>
        );
    }
);

export default AppSidebarV2;
